import { useState, useCallback } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { gql } from 'graphql-request'
import { MessageSquareIcon, ExternalLinkIcon, Check, ChevronsUpDown } from 'lucide-react'
import { Link } from 'react-router-dom'
import { truncate, debounce } from 'lodash'

import { useQuery } from '@hooks/graphql'
import { stripMarkdown } from '@helpers/format'
import { Button } from '@components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@components/ui/dialog'
import Image from '@components/Image'
import { Popover, PopoverTrigger, PopoverContent } from '@components/ui/popover'
import { Command, CommandInput, CommandList, CommandGroup, CommandItem } from '@components/ui/command'
import { twMerge } from 'tailwind-merge'
import { useEducatorProject, useUpdateEducatorProject } from '../hooks'

const formatPrompt = prompt => truncate(stripMarkdown(prompt), { length: 100 })

const TUTORS_QUERY = gql`
  query tutors($audience: AudienceEnum!, $search: String, $tags: [String!], $page: Int) {
    tutors(audience: $audience, search: $search, tags: $tags, page: $page, perPage: 10) {
      nodes {
        id
        name
        description
        summary
        visibility
        image {
          url
        }
      }
      pagesCount
      nodesCount
    }
  }
`

const ComboBox = ({ value, onChange, tutors, debouncedSetSearchTerm, isSearching, searchTerm, formatPrompt }) => {
  const [open, setOpen] = useState(false)
  const [noTutorSelected, setNoTutorSelected] = useState(value === null)

  return (
    <Popover className='w-full' open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
        >
          {value ? value.name : (noTutorSelected ? "No tutor" : "Select a tutor...")}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align='start'>
        <Command filter={() => 1}>
          <CommandInput
            className='w-full'
            placeholder="Search tutors..."
            onValueChange={(search) => debouncedSetSearchTerm(search)}
          />
          <CommandList>
            <CommandGroup>
              <CommandItem
                key="no-tutor"
                value="no-tutor"
                onSelect={() => {
                  onChange(null)
                  setNoTutorSelected(true)
                  setOpen(false)
                }}
                className="cursor-pointer"
              >
                <div className='flex items-center w-full'>
                  <div className='leading-tight'>
                    <p className='text-sm font-medium leading-none'>No tutor</p>
                    <small className='text-xs text-gray-500'>Students won't have AI assistance</small>
                  </div>
                </div>
                <Check
                  className={twMerge(
                    "mr-2 h-4 w-4",
                    value === null ? "opacity-100" : "opacity-0"
                  )}
                />
              </CommandItem>
              {tutors.map((currentTutor) => (
                <CommandItem
                  key={currentTutor.id}
                  value={currentTutor.id}
                  onSelect={() => {
                    const tutorToSelect = tutors.find(t => t.id === currentTutor.id)
                    if (value?.id === currentTutor.id) {
                      onChange(null)
                      setNoTutorSelected(true)
                    } else {
                      onChange(currentTutor)
                      setNoTutorSelected(false)
                    }
                    setOpen(false)
                  }}
                  className="cursor-pointer"
                >
                  <div className='flex items-center w-full'>
                    <Choose>
                      <When condition={currentTutor.image?.url}>
                        <Image
                          src={currentTutor.image?.url}
                          alt={currentTutor.name}
                          className='size-8 rounded-md mr-2 object-cover shrink-0'
                        />
                      </When>
                      <Otherwise>
                        <div className='flex items-center justify-center size-8 rounded-md mr-2 bg-gray-100 shrink-0'>
                          <MessageSquareIcon className='size-4 text-gray-400' />
                        </div>
                      </Otherwise>
                    </Choose>
                    <div className='leading-tight truncate'>
                      <p className='text-sm leading-none'>{currentTutor.name}</p>
                      <small className='text-xs text-gray-500'>
                        <Choose>
                          <When condition={currentTutor.summary}>
                            {truncate(currentTutor.summary, { length: 40 })}
                          </When>
                          <Otherwise>
                            {formatPrompt(currentTutor.description)}
                          </Otherwise>
                        </Choose>
                      </small>
                    </div>
                  </div>
                  <Check
                    className={twMerge(
                      "mr-2 h-4 w-4",
                      value?.id === currentTutor.id ? "opacity-100" : "opacity-0"
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>

            <If condition={tutors.length === 0}>
              <Choose>
                <When condition={isSearching && searchTerm && searchTerm.trim() !== ''}>
                  <div className="py-6 text-center text-sm text-gray-500">Searching...</div>
                </When>
                <When condition={!isSearching && searchTerm && searchTerm.trim() !== ''}>
                  <div className="py-6 text-center text-sm text-gray-500">No tutors found for "{searchTerm}".</div>
                </When>
                <Otherwise>
                  <div className="py-6 text-center text-sm text-gray-500">No tutors available.</div>
                </Otherwise>
              </Choose>
            </If>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

const TutorSelectionModal = ({ id, closeModal, isOpen, tutor }) => {
  const { refetch } = useEducatorProject({ id })
  const { updateProject, status } = useUpdateEducatorProject({
    id,
    onSuccess: () => {
      refetch()
      closeModal()
    }
  })

  const [searchTerm, setSearchTerm] = useState(null)

  const { data: { tutors: { nodes: tutors = [] } = {} } = {}, isFetching: isSearching } = useQuery({
    queryKey: ['tutors', 'ME', searchTerm],
    gqlQuery: TUTORS_QUERY,
    variables: { audience: 'ME', page: 1, search: searchTerm },
    staleTime: 0,
    cacheTime: 0
  })

  const debouncedSetSearchTerm = useCallback(
    debounce((text) => setSearchTerm(text), 300),
    []
  )

  const { control, watch, handleSubmit, formState: { errors } } = useForm({
    mode: 'onTouched',
    defaultValues: { tutor }
  })

  const submit = ({ tutor }) => {
    updateProject({ tutorId: tutor?.id || null })
  }

  const selectedTutor = watch('tutor')

  return (
    <Dialog
      open={isOpen}
      onOpenChange={closeModal}
    >
      <DialogContent className='sm:max-w-[425px]'>
        <DialogHeader>
          <DialogTitle>Select a Tutor</DialogTitle>
        </DialogHeader>
        <DialogDescription>
          Tutors are AI helpers students can chat with during lessons. Without one, students won't have AI assistance while working.
        </DialogDescription>
        <div className='flex flex-col h-full'>
          <form
            id='tutor-form'
            onSubmit={handleSubmit(submit)}
            className='flex flex-col flex-grow'
          >
            <div className='mb-3'>
              <Controller
                name='tutor'
                control={control}
                render={({ field }) => (
                  <ComboBox
                    value={field.value}
                    onChange={field.onChange}
                    tutors={tutors}
                    debouncedSetSearchTerm={debouncedSetSearchTerm}
                    isSearching={isSearching}
                    searchTerm={searchTerm}
                    formatPrompt={formatPrompt}
                  />
                )}
              />
              <If condition={errors.tutor}>
                <p className='text-sm mt-1 text-red-500 font-semibold' role='alert'>{errors.tutor.message}</p>
              </If>
            </div>

            <Choose>
              <When condition={!selectedTutor}>
                <p className='flex text-sm'>
                  Or create a&nbsp;
                  <Link
                    className='flex items-center underline'
                    target='_blank'
                    to={`/educators/tutors?new=true`}
                  >
                    new tutor
                    <ExternalLinkIcon className='size-4 ml-1' />
                  </Link>
                </p>
              </When>
            </Choose>
          </form>
        </div>
        <DialogFooter className='mt-auto pt-4'>
          <Button
            variant='ghost'
            onClick={closeModal}
            type='button'
          >
            Cancel
          </Button>
          <Button
            disabled={status === 'loading'}
            type='submit'
            form='tutor-form'
            className='w-fit'
          >
            {status === 'loading' ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default TutorSelectionModal
